/**
 * Fertige Prüfungen zu einer Rechnung sammeln.
 *
 * Der Weg, den diese Seite abbildet, ist der aus dem Büroalltag: Bernd
 * prüft für eine Gemeinde über den Sommer verteilt zwölf Plätze, und am
 * Ende geht eine Rechnung hinaus. Bis hierher hieß das zwölf Zeilen von
 * Hand – und selbst nachzuhalten, welcher Platz schon auf einer früheren
 * Rechnung stand.
 *
 * Zwei Dinge macht die Seite deshalb ausdrücklich **nicht**:
 *
 *  - Sie rechnet nichts nach. Ob eine Prüfung offen ist, entscheidet der
 *    Server anhand der Rechnungszeilen. Eine zweite Wahrheit in der
 *    Oberfläche wäre eine, die irgendwann abweicht.
 *  - Sie schickt nichts los. Am Ende steht ein **Entwurf**. Bernd sieht
 *    ihn durch, ändert Preise oder Texte, und erst das Herausgeben
 *    vergibt die Nummer.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useOffeneKunden,
  useOffenePruefungen,
  useSammeln,
  type OffenePruefung,
} from '../api/abrechnung'
import { euro } from '../api/belege'
import { PRUEFARTTEXT } from '../api/typen'
import { Blatt } from '../bausteine/Blatt'
import {
  Fehlerhinweis,
  Ladeanzeige,
  Leerhinweis,
  datumDeutsch,
} from '../bausteine/Hinweise'

export function Abrechnung() {
  const kunden = useOffeneKunden()
  const [gewaehlt, setzeGewaehlt] = useState<{ id: string; name: string } | null>(null)

  if (kunden.isLoading) return <Ladeanzeige />
  if (kunden.error) return <Fehlerhinweis fehler={kunden.error} />

  const liste = kunden.data ?? []
  const gesamt = liste.reduce((summe, k) => summe + k.anzahl, 0)

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Abrechnen</h1>
        <p className="text-base text-slate-700">
          Abgeschlossene Prüfungen, die auf keiner Rechnung stehen. Ein Kunde,
          eine Rechnung.
        </p>
      </div>

      {liste.length === 0 ? (
        <Leerhinweis
          titel="Nichts offen"
          text="Alle abgeschlossenen Prüfungen stehen auf einer Rechnung. Sobald eine Prüfung abgeschlossen wird, erscheint sie hier."
        />
      ) : (
        <>
          <p className="text-base text-slate-600">
            {gesamt} {gesamt === 1 ? 'Prüfung' : 'Prüfungen'} bei {liste.length}{' '}
            {liste.length === 1 ? 'Kunden' : 'Kunden'}.
          </p>

          <ul className="space-y-2">
            {liste.map((kunde) => (
              <li key={kunde.kunde_id}>
                <button
                  type="button"
                  className="karte karte-knopf w-full text-left"
                  onClick={() =>
                    setzeGewaehlt({ id: kunde.kunde_id, name: kunde.kunde_name })
                  }
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-semibold">{kunde.kunde_name}</span>
                    <span className="marke bg-akzent-grund text-akzent-700">
                      {kunde.anzahl}{' '}
                      {kunde.anzahl === 1 ? 'Prüfung' : 'Prüfungen'}
                    </span>
                  </div>
                  <p className="mt-1 text-base text-slate-600">
                    {kunde.von === kunde.bis
                      ? datumDeutsch(kunde.von)
                      : `${datumDeutsch(kunde.von)} – ${datumDeutsch(kunde.bis)}`}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {gewaehlt && (
        <Sammelblatt
          kundeId={gewaehlt.id}
          kundeName={gewaehlt.name}
          schliessen={() => setzeGewaehlt(null)}
        />
      )}
    </div>
  )
}

function Sammelblatt({
  kundeId,
  kundeName,
  schliessen,
}: {
  kundeId: string
  kundeName: string
  schliessen: () => void
}) {
  const pruefungen = useOffenePruefungen(kundeId)
  const sammeln = useSammeln()
  const navigieren = useNavigate()
  // Alles vorausgewählt: der Normalfall ist „alles abrechnen". Wer eine
  // Prüfung noch nicht berechnen will, nimmt sie heraus — das ist der
  // seltenere Griff und darf der aufwendigere sein.
  const [abgewaehlt, setzeAbgewaehlt] = useState<Set<string>>(new Set())

  const alle = pruefungen.data ?? []
  const gewaehlt = alle.filter((p) => !abgewaehlt.has(p.pruefung_id))
  const summe = gewaehlt.reduce(
    (s, p) => s + Number(p.vorschlag_einzelpreis ?? 0),
    0,
  )
  const ohnePreis = gewaehlt.filter((p) => p.vorschlag_einzelpreis === null).length

  function umschalten(kennung: string) {
    setzeAbgewaehlt((vorher) => {
      const neu = new Set(vorher)
      if (neu.has(kennung)) neu.delete(kennung)
      else neu.add(kennung)
      return neu
    })
  }

  return (
    <Blatt
      titel={kundeName}
      untertitel={`${gewaehlt.length} von ${alle.length} Prüfungen auf die Rechnung`}
      schliessen={schliessen}
      breit
      fuss={
        <div className="flex gap-3">
          <button type="button" className="knopf-still flex-1" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="button"
            className="knopf-haupt flex-1"
            disabled={sammeln.isPending || gewaehlt.length === 0}
            onClick={() =>
              sammeln.mutate(
                {
                  kunde_id: kundeId,
                  pruefung_ids: gewaehlt.map((p) => p.pruefung_id),
                },
                { onSuccess: (beleg) => navigieren(`/belege/${beleg.id}`) },
              )
            }
          >
            {sammeln.isPending ? 'Sammelt …' : 'Rechnungsentwurf anlegen'}
          </button>
        </div>
      }
    >
      {pruefungen.isLoading ? (
        <Ladeanzeige />
      ) : pruefungen.error ? (
        <Fehlerhinweis fehler={pruefungen.error} />
      ) : (
        <div className="space-y-3">
          <p className="rounded-feld border-2 border-marke-100 bg-marke-50 p-3 text-marke-700">
            Es entsteht ein <span className="font-semibold">Entwurf</span>. Nichts
            geht dabei hinaus, und eine Nummer bekommt er erst beim Herausgeben.
          </p>

          {ohnePreis > 0 && (
            <p className="rounded-feld border-2 border-stufe-2 bg-stufe-2-grund p-3 text-stufe-2">
              Für {ohnePreis}{' '}
              {ohnePreis === 1 ? 'Prüfung steht' : 'Prüfungen stehen'} kein Preis
              im Leistungskatalog. Diese{' '}
              {ohnePreis === 1 ? 'Zeile entsteht' : 'Zeilen entstehen'} mit
              0,00 € — bitte im Entwurf ausfüllen. Geraten wird nichts.
            </p>
          )}

          <ul className="space-y-2">
            {alle.map((p) => (
              <Zeile
                key={p.pruefung_id}
                pruefung={p}
                gewaehlt={!abgewaehlt.has(p.pruefung_id)}
                umschalten={() => umschalten(p.pruefung_id)}
              />
            ))}
          </ul>

          <div className="flex items-baseline justify-between border-t-2 border-slate-200 pt-3">
            <span className="font-semibold">Netto nach Katalog</span>
            <span className="text-xl font-bold tabular-nums">{euro(summe)}</span>
          </div>

          <Fehlerhinweis fehler={sammeln.error} />
        </div>
      )}
    </Blatt>
  )
}

function Zeile({
  pruefung,
  gewaehlt,
  umschalten,
}: {
  pruefung: OffenePruefung
  gewaehlt: boolean
  umschalten: () => void
}) {
  return (
    <li>
      <label
        className={`flex items-start gap-3 rounded-feld border-2 p-3 ${
          gewaehlt ? 'border-akzent-rand bg-akzent-grund' : 'border-slate-200'
        }`}
      >
        <input
          type="checkbox"
          className="mt-1 h-6 w-6"
          checked={gewaehlt}
          onChange={umschalten}
        />
        <span className="min-w-0 flex-1">
          <span className="block font-semibold">
            {pruefung.spielplatz_name}
            {pruefung.ort ? `, ${pruefung.ort}` : ''}
          </span>
          <span className="block text-base text-slate-600">
            {PRUEFARTTEXT[pruefung.pruefart]} · {datumDeutsch(pruefung.datum)}
            {pruefung.berichtsnummer ? ` · ${pruefung.berichtsnummer}` : ''}
          </span>
        </span>
        <span className="shrink-0 text-right">
          {pruefung.vorschlag_einzelpreis === null ? (
            <span className="marke bg-stufe-2-grund text-stufe-2">kein Preis</span>
          ) : (
            <span className="font-semibold tabular-nums">
              {euro(pruefung.vorschlag_einzelpreis)}
            </span>
          )}
        </span>
      </label>
    </li>
  )
}
